import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonItem,
  IonLabel,
  IonToggle,
} from '@ionic/react';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useState,
  useEffect,
} from 'react';
import Trans from 'util/Trans';
import DynamicButton from 'components/buttons/DynamicButton';
import { useStores } from 'models';
import { observer } from 'mobx-react-lite';
import { SERVER_URL_ALPHA, SERVER_URL_BETA } from 'util/constants';

export interface SettingsModalProps extends ComponentProps<typeof IonModal> {
  onCommit: () => Promise<void>;
}

const SettingsModal: FunctionComponent<SettingsModalProps> = observer(
  ({ onCommit, ...rest }) => {
    const modalRef = useRef<HTMLIonModalElement>(null);
    const [isCommitting, setIsCommitting] = useState(false);
    const { appData } = useStores();
    const { displaySettings } = appData;

    // Local state for form data
    const [formData, setFormData] = useState<{
      vehicleType: 'skateboard' | 'scooter' | 'bike' | 'custom';
      showTrajectory: boolean;
      showGround: boolean;
      showGroundMesh: boolean;
      showGForce: boolean;
      activateAutoEstimate: boolean;
      weatherUnits: 'metric' | 'imperial';
      defaultViewMode: 'score' | 'runstats';
      serverUrl: string;
      downloadType:
        | 'only_video'
        | 'only_telemetry'
        | 'only_steps'
        | 'only_figures'
        | 'only_details'
        | 'all';
    }>({
      vehicleType: displaySettings.vehicleType as
        | 'skateboard'
        | 'scooter'
        | 'bike'
        | 'custom',
      showTrajectory: displaySettings.showTrajectory,
      showGround: displaySettings.showGround,
      showGroundMesh: displaySettings.showGroundMesh,
      showGForce: displaySettings.showGForce,
      activateAutoEstimate: displaySettings.activateAutoEstimate,
      weatherUnits: displaySettings.weatherUnits as 'metric' | 'imperial',
      defaultViewMode:
        (displaySettings.defaultViewMode as 'score' | 'runstats') || 'score',
      serverUrl: displaySettings.serverUrl || SERVER_URL_ALPHA,
  downloadType: (displaySettings.downloadType as 'only_video' | 'only_telemetry' | 'only_steps' | 'only_figures' | 'only_details' | 'all') || 'all',
    });

    // Sync form data with store when modal opens
    useEffect(() => {
      setFormData({
        vehicleType: displaySettings.vehicleType as
          | 'skateboard'
          | 'scooter'
          | 'bike'
          | 'custom',
        showTrajectory: displaySettings.showTrajectory,
        showGround: displaySettings.showGround,
        showGroundMesh: displaySettings.showGroundMesh,
        showGForce: displaySettings.showGForce,
        activateAutoEstimate: displaySettings.activateAutoEstimate,
        weatherUnits: displaySettings.weatherUnits as 'metric' | 'imperial',
        defaultViewMode:
          (displaySettings.defaultViewMode as 'score' | 'runstats') || 'score',
        serverUrl: displaySettings.serverUrl || SERVER_URL_ALPHA,
  downloadType: (displaySettings.downloadType as 'only_video' | 'only_telemetry' | 'only_steps' | 'only_figures' | 'only_details' | 'all') || 'all',
      });
    }, [displaySettings]);

    const dismiss = () => {
      modalRef.current?.dismiss();
    };

    const updateSettings = (): void => {
      displaySettings.setVehicleType(formData.vehicleType);
      displaySettings.setShowTrajectory(formData.showTrajectory);
      displaySettings.setShowGround(formData.showGround);
      displaySettings.setShowGroundMesh(formData.showGroundMesh);
      displaySettings.setShowGForce(formData.showGForce);
      displaySettings.setActivateAutoEstimate(formData.activateAutoEstimate);
      displaySettings.setWeatherUnits(formData.weatherUnits);
      displaySettings.setDefaultViewMode(formData.defaultViewMode);
      displaySettings.setServerUrl(formData.serverUrl);
      displaySettings.setDownloadType(formData.downloadType);
    };

    const _onCommit = () => {
      setIsCommitting(true);
      updateSettings();
      onCommit().finally(() => {
        setIsCommitting(false);
      });
    };

    return (
      <IonModal {...rest} ref={modalRef} className="edit-modal">
        <IonHeader className="ion-no-border">
          <IonToolbar className="edit-modal-toptoolbar">
            <IonTitle style={{ textAlign: 'center' }}>
              <Trans i18nKey="SETTINGS">Settings</Trans>
            </IonTitle>
            <IonButtons slot="start">
              <DynamicButton
                text={<Trans i18nKey="OK" />}
                iconName="OKUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                disabled={isCommitting}
                onClick={_onCommit}
              />
            </IonButtons>
            <IonButtons slot="end">
              <DynamicButton
                text={<Trans i18nKey="CANCEL" />}
                iconName="CancelUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                onClick={dismiss}
              />
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent
          style={{
            '--background': 'transparent',
            '--border-colour': 0,
          }}
        >
          <div className="edit-modal-wrapper">
            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="VEHICLE_TYPE">Vehicle Type</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="SELECT_VEHICLE_TYPE">
                      Select Vehicle Type
                    </Trans>
                  </IonLabel>
                  <select
                    value={formData.vehicleType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        vehicleType: e.target.value as
                          | 'skateboard'
                          | 'scooter'
                          | 'bike'
                          | 'custom',
                      }));
                    }}
                  >
                    <option value="skateboard">
                      <Trans i18nKey="SKATEBOARD">Skateboard</Trans>
                    </option>
                    <option value="scooter">
                      <Trans i18nKey="SCOOTER">Scooter</Trans>
                    </option>
                    <option value="bike">
                      <Trans i18nKey="BIKE">Bike</Trans>
                    </option>
                    <option value="custom">
                      <Trans i18nKey="CUSTOM">Custom</Trans>
                    </option>
                  </select>
                </IonItem>
              </div>
            </div>

            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="DISPLAY_SETTINGS">Display Settings</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                {/* Show Trajectory Toggle */}
                <IonItem>
                  <IonLabel>
                    <Trans i18nKey="SHOW_TRAJECTORY">Show Trajectory</Trans>
                  </IonLabel>
                  <IonToggle
                    checked={formData.showTrajectory}
                    onIonChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        showTrajectory: e.detail.checked,
                      }));
                    }}
                  />
                </IonItem>

                {/* Show Ground Toggle */}
                <IonItem>
                  <IonLabel>
                    <Trans i18nKey="SHOW_GROUND">Show Ground (Plain)</Trans>
                  </IonLabel>
                  <IonToggle
                    checked={formData.showGround}
                    onIonChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        showGround: e.detail.checked,
                      }));
                    }}
                  />
                </IonItem>
                {/* Show Ground Mesh Toggle */}
                <IonItem>
                  <IonLabel>
                    <Trans i18nKey="SHOW_GROUND_MESH">Show Ground (Mesh)</Trans>
                  </IonLabel>
                  <IonToggle
                    checked={formData.showGroundMesh}
                    onIonChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        showGroundMesh: e.detail.checked,
                      }));
                    }}
                  />
                </IonItem>

                {/* Show G-Force Toggle */}
                <IonItem>
                  <IonLabel>
                    <Trans i18nKey="SHOW_GFORCE">Show G-Force</Trans>
                  </IonLabel>
                  <IonToggle
                    checked={formData.showGForce}
                    onIonChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        showGForce: e.detail.checked,
                      }));
                    }}
                  />
                </IonItem>
              </div>
            </div>

            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="ESTIMATOR_SETTINGS">Estimator</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel>
                    <Trans i18nKey="ACTIVATE_AUTO_ESTIMATE">
                      Activate Auto Estimate
                    </Trans>
                  </IonLabel>
                  <IonToggle
                    checked={formData.activateAutoEstimate}
                    onIonChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        activateAutoEstimate: e.detail.checked,
                      }));
                    }}
                  />
                </IonItem>
              </div>
            </div>

            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="WEATHER_SETTINGS">Weather Settings</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                {/* Weather Units Select */}
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="UNITS">Units</Trans>
                  </IonLabel>
                  <select
                    value={formData.weatherUnits}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        weatherUnits: e.target.value as 'metric' | 'imperial',
                      }));
                    }}
                  >
                    <option value="metric">
                      <Trans i18nKey="METRIC">Metric (km/h)</Trans>
                    </option>
                    <option value="imperial">
                      <Trans i18nKey="IMPERIAL">Imperial (mph)</Trans>
                    </option>
                  </select>
                </IonItem>
              </div>
            </div>

            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="VIEW_SETTINGS">View Settings</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                {/* Default View Mode Select */}
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="DEFAULT_VIEW_MODE">Default View Mode</Trans>
                  </IonLabel>
                  <select
                    value={formData.defaultViewMode}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        defaultViewMode: e.target.value as 'score' | 'runstats',
                      }));
                    }}
                  >
                    <option value="score">
                      <Trans i18nKey="SCORE_VIEW">Score View</Trans>
                    </option>
                    <option value="runstats">
                      <Trans i18nKey="RUN_STATS_BOX">Run Stats Box</Trans>
                    </option>
                  </select>
                </IonItem>
              </div>
            </div>

            <div className="edit-modal-group">
              <IonLabel>
                <Trans i18nKey="SERVER_SETTINGS">Server</Trans>
              </IonLabel>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="SELECT_SERVER">Select Server</Trans>
                  </IonLabel>
                  <select
                    value={formData.serverUrl}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        serverUrl: e.target.value,
                      }));
                    }}
                  >
                    <option value={SERVER_URL_ALPHA}>Alpha</option>
                    <option value={SERVER_URL_BETA}>Beta</option>
                  </select>
                </IonItem>
              </div>
            </div>

            {process.env.REACT_APP_IS_WEB === 'true' && (
              <div className="edit-modal-group">
                <IonLabel>
                  <Trans i18nKey="DOWNLOAD_SETTINGS">Download Settings</Trans>
                </IonLabel>
                <div className="edit-modal-innergroup">
                  <IonItem>
                    <IonLabel position="stacked">
                      <Trans i18nKey="DOWNLOAD_TYPE">Download Type</Trans>
                    </IonLabel>
                    <select
                      value={formData.downloadType}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setFormData((prev) => ({
                          ...prev,
                          downloadType: e.target.value as any,
                        }));
                      }}
                    >
                      <option value="all">
                        <Trans i18nKey="ALL">All</Trans>
                      </option>
                      <option value="only_video">
                        <Trans i18nKey="ONLY_VIDEO">Video only</Trans>
                      </option>
                      <option value="only_telemetry">
                        <Trans i18nKey="ONLY_TELEMETRY">Telemetry only</Trans>
                      </option>
                      <option value="only_steps">
                        <Trans i18nKey="ONLY_STEPS">Steps only</Trans>
                      </option>
                      <option value="only_figures">
                        <Trans i18nKey="ONLY_FIGURES">Figures only</Trans>
                      </option>
                      <option value="only_details">
                        <Trans i18nKey="ONLY_DETAILS">Details only</Trans>
                      </option>
                    </select>
                  </IonItem>
                </div>
              </div>
            )}
          </div>
        </IonContent>
      </IonModal>
    );
  }
);

export default SettingsModal;
